import React, { useEffect } from 'react';
import './styles/video-to-images.css';
import VideoTargetSelector from './components/VideoTargetSelector';
import VideoUploadZone from './components/VideoUploadZone';
import ExtractionOptionsForm from './components/ExtractionOptionsForm';
import VideoJobsList from './components/VideoJobsList';
import VideoJobDetailModal from './components/VideoJobDetailModal';
import DatasetPanel from './components/DatasetPanel';
import BuildDatasetModal from './components/BuildDatasetModal';
import useVideoToImagesHook, { isJobActive } from './hooks/useVideoToImagesHook';
import { isBuildActive } from './components/DatasetBuildCard';
import useVideoToImagesStore from './store/useVideoToImagesStore';
import { useI18n } from '../../../i18n';

// Same cadence the model pages use: fast while work is in flight, slow when idle.
const ACTIVE_REFRESH_MS = 5000;
const IDLE_REFRESH_MS = 20000;

/**
 * Video → Images — dataset preparation, upstream of labeling.
 *
 * Extraction runs on the `video` Celery queue, so the user can start a job and
 * leave; on return the jobs list is rebuilt from the persisted job records.
 */
const VideoToImages = () => {
  const { t } = useI18n();
  const {
    engine, supportedExtensions,
    targetMode, datasetName,
    projects, models, selectedProjectId, selectedModelId,
    selectedFiles, dragOver,
    isUploading, uploadPercent, uploadedBytes,
    options, jobs, jobsError, isStarting,
    detailJob, detailLogs,
    datasets, datasetsError, activeDataset, datasetItems, datasetTotal,
    isDatasetLoading, selectedItemIds, isDownloading,
    transferProjectId, transferModelId, transferVersionId,
    transferModels, modelVersions,
    creatingProject, newProjectName, creatingModel, newModelName, newModelType,
    isCreatingTarget,
    isBuildOpen, versionTarget, versionUnlabeled,
    buildTask, buildClassMode, buildClassCount, buildClassNames,
    buildObjects, buildConfidence, isStartingBuild, builds,
    handlers,
  } = useVideoToImagesHook();

  // Initial load.
  useEffect(() => {
    handlers.handleGetEngineInfo();
    handlers.handleGetProjects();
    handlers.handleGetJobs();
    handlers.handleGetDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Self-rescheduling poll — reads the store directly so the effect does not
  // re-subscribe on every job update.
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const scheduleNextRefresh = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (cancelled) return;

      const state = useVideoToImagesStore.getState();
      const currentJobs = state.jobs;
      const currentBuilds = state.builds;
      const hasActiveJobs =
        (Array.isArray(currentJobs) && currentJobs.some(isJobActive))
        || (Array.isArray(currentBuilds) && currentBuilds.some(isBuildActive));

      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        try {
          await handlers.handleGetJobs();
          // A finishing job or build changes dataset counts, so keep those
          // fresh too — and a build's own progress is what the card renders.
          if (hasActiveJobs) {
            await handlers.handleGetDatasets();
            const open = useVideoToImagesStore.getState().activeDataset;
            if (open?._id) await handlers.handleGetBuilds(open._id);
          }
        } catch (error) {
          // noop — the next tick retries
        } finally {
          scheduleNextRefresh();
        }
      }, hasActiveJobs ? ACTIVE_REFRESH_MS : IDLE_REFRESH_MS);
    };

    scheduleNextRefresh();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasTarget = targetMode === 'dataset'
    ? Boolean(datasetName.trim())
    : Boolean(selectedProjectId && selectedModelId);
  const canStart = Boolean(hasTarget && selectedFiles.length);

  return (
    <div className="container v2i-page">
      <div className="col-md-12 mt-2">
        <div className="v2i-page__head">
          <h4 className="v2i-page__title mb-1">{t('videoToImages.title')}</h4>
          <p className="v2i-page__subtitle mb-0">{t('videoToImages.subtitle')}</p>
        </div>
      </div>

      <div className="col-md-12 mt-2">
        <VideoTargetSelector
          targetMode={targetMode}
          datasetName={datasetName}
          datasets={datasets}
          projects={projects}
          models={models}
          selectedProjectId={selectedProjectId}
          selectedModelId={selectedModelId}
          engine={engine}
          creatingProject={creatingProject}
          newProjectName={newProjectName}
          creatingModel={creatingModel}
          newModelName={newModelName}
          newModelType={newModelType}
          isCreatingTarget={isCreatingTarget}
          onSelectProject={handlers.handleSelectProject}
          onSelectModel={handlers.setSelectedModelId}
          handlers={handlers}
        />
      </div>

      <div className="col-md-12 mt-2">
        <div className="row">
          <div className="col-lg-7 mb-2">
            <VideoUploadZone
              selectedFiles={selectedFiles}
              supportedExtensions={supportedExtensions}
              dragOver={dragOver}
              isUploading={isUploading}
              uploadPercent={uploadPercent}
              uploadedBytes={uploadedBytes}
              handlers={handlers}
            />
          </div>
          <div className="col-lg-5 mb-2">
            <ExtractionOptionsForm
              options={options}
              isStarting={isStarting}
              isUploading={isUploading}
              canStart={canStart}
              handlers={handlers}
              onStart={handlers.handleStartConversion}
            />
          </div>
        </div>
      </div>

      <div className="col-md-12 mt-2">
        <DatasetPanel
          datasets={datasets}
          builds={builds}
          datasetsError={datasetsError}
          activeDataset={activeDataset}
          datasetItems={datasetItems}
          datasetTotal={datasetTotal}
          isDatasetLoading={isDatasetLoading}
          isDownloading={isDownloading}
          selectedItemIds={selectedItemIds}
          handlers={handlers}
        />
      </div>

      <div className="col-md-12 mt-2">
        <VideoJobsList jobs={jobs} jobsError={jobsError} handlers={handlers} />
      </div>

      <VideoJobDetailModal
        job={detailJob}
        logs={detailLogs}
        onClose={handlers.handleCloseDetails}
      />

      <BuildDatasetModal
        isOpen={isBuildOpen}
        versionTarget={versionTarget}
        versionUnlabeled={versionUnlabeled}
        datasetName={activeDataset?.name || ''}
        selectedCount={selectedItemIds.size}
        projects={projects}
        transferModels={transferModels}
        modelVersions={modelVersions}
        transferProjectId={transferProjectId}
        transferModelId={transferModelId}
        transferVersionId={transferVersionId}
        creatingProject={creatingProject}
        newProjectName={newProjectName}
        creatingModel={creatingModel}
        newModelName={newModelName}
        newModelType={newModelType}
        buildTask={buildTask}
        buildClassMode={buildClassMode}
        buildClassCount={buildClassCount}
        buildClassNames={buildClassNames}
        buildObjects={buildObjects}
        buildConfidence={buildConfidence}
        isStartingBuild={isStartingBuild}
        handlers={handlers}
      />
    </div>
  );
};

export default VideoToImages;
